<script lang="ts" setup>
import {
  Media,
  descriptionRules,
  purposeRules,
  purposeItems,
} from '@/models/media.model';
import { Mutations } from '@/store';
import { PropType, ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Media>,
    required: true,
  },
});

const store = useStore();
const media = ref<Media>({ ...props.modelValue });
const { t } = useI18n();

const emit = defineEmits(['update:modelValue', 'change']);

function update() {
  emit('update:modelValue', media.value);
  emit('change', media.value);
}

const currentValue = { ...props.modelValue };

onMounted(() => {
  Object.assign(media.value, { ...props.modelValue });
});

watch(
  () => props.modelValue,
  () => {
    Object.assign(media.value, { ...props.modelValue });
  },
  { deep: true },
);

function getSimpleValue(i: Media) {
  return {
    title: i.description,
    purpose: i.purpose,
    language: i.language,
  };
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(media.value)) !==
    JSON.stringify(getSimpleValue(currentValue));

  store.commit(Mutations.SetDirtyState, isDirty);

  return isDirty;
});

const languageItems = [
  { title: t('media.all'), value: 'all' },
  { title: 'Nederlands', value: 'nl' },
  { title: 'English', value: 'en' },
];
</script>
<template>
  <v-row>
    <v-col cols="11">
      <v-text-field
        v-model="media.description"
        @update:model-value="update"
        :rules="descriptionRules"
        :label="$t('media.description')"
        required
      ></v-text-field>
    </v-col>
    <v-col>
      <span v-if="isDirty">*</span>
    </v-col>
  </v-row>
  <v-row>
    <v-col>
      <v-select
        v-model="media.purpose"
        @update:model-value="update"
        :rules="purposeRules"
        :items="purposeItems"
        :label="$t('media.purpose')"
        required
      >
      </v-select>
    </v-col>
    <v-col v-if="media.file && media.id !== -1">
      <v-select
        v-model="media.file.language"
        @update:model-value="update"
        :items="languageItems"
        :label="$t('media.language')"
        required
      >
      </v-select>
    </v-col>
    <v-col v-if="media.id === -1 && media.file?.uuid">
      <v-select
        v-model="media.file.language"
        @update:model-value="update"
        :items="languageItems"
        :label="$t('media.language')"
        title="(File)"
        required
        readonly
        disabled
      >
      </v-select>
    </v-col>
    <v-col v-if="media.id === -1 && !media.file?.uuid">
      <v-select
        v-model="media.language"
        @update:model-value="update"
        :items="languageItems"
        :label="$t('media.language')"
        title="(Media)"
        required
      >
      </v-select>
    </v-col>
  </v-row>
</template>
